import React, { useEffect, useState, useCallback, useRef } from 'react';
import { host } from '../script/variables';
import AdminDocumentPreview from './AdminDocumentPreview';
import '../style/AdminDocumentsList.css';

const LIMIT = 20;

const TABS = [
  { key: 'pending',  label: 'Pending',  icon: '⏳' },
  { key: 'partial',  label: 'Partial',  icon: '⚠️' },
  { key: 'verified', label: 'Verified', icon: '✅' },
  { key: 'rejected', label: 'Rejected', icon: '❌' },
  { key: 'all',      label: 'All',      icon: '📁' },
];

const CATEGORIES = [
  { key: '',           label: 'All Categories' },
  { key: 'owner',       label: 'Owner Docs' },
  { key: 'restaurant',  label: 'Restaurant Docs' },
  { key: 'other',       label: 'Other' },
];

const timeAgo = (timestamp) => {
  if (!timestamp) return '—';
  const diff   = Date.now() - new Date(timestamp);
  const mins   = Math.floor(diff / 60000);
  const hours  = Math.floor(mins  / 60);
  const days   = Math.floor(hours / 24);
  const months = Math.floor(days  / 30);
  if (months > 0) return `${months}mo ago`;
  if (days   > 0) return `${days}d ago`;
  if (hours  > 0) return `${hours}h ago`;
  if (mins   > 0) return `${mins}m ago`;
  return 'just now';
};

const STATUS_META = {
  not_submitted:      { label: 'Not Submitted', cls: 'muted' },
  pending:             { label: 'Pending',       cls: 'warn'  },
  verified:            { label: 'Verified',      cls: 'good'  },
  rejected:            { label: 'Rejected',      cls: 'bad'   },
  partially_verified:  { label: 'Partial',       cls: 'warn'  },
};

/* ─── Skeleton ───────────────────────────────────────────── */
const SkeletonCard = () => (
  <div className="dl-card dl-card--skeleton">
    <div className="dl-skel dl-skel--tag" />
    <div className="dl-skel dl-skel--title" />
    <div className="dl-skel dl-skel--title dl-skel--short" />
    <div className="dl-skel dl-skel--meta" />
  </div>
);

/* ─── Card ───────────────────────────────────────────────── */
const DocCard = ({ doc, onSelect }) => {
  const meta = STATUS_META[doc.verificationStatus] || STATUS_META.pending;
  const fileCount = doc.fileCount ?? Object.keys(doc.files || {}).length;
  const latestUpload = Object.values(doc.files || {}).reduce((max, f) => {
    const t = new Date(f.uploadedAt).getTime();
    return t > max ? t : max;
  }, 0);

  return (
    <article
      className="dl-card"
      onClick={() => onSelect(doc)}
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onSelect(doc)}
    >
      <div className="dl-card__top">
        <span className={`dl-badge dl-badge--${meta.cls}`}>{meta.label}</span>
        <span className="dl-card__cat">{doc.category}</span>
      </div>

      <h3 className="dl-card__title">
        {doc.partner?.displayName
          || [doc.partner?.firstName, doc.partner?.lastName].filter(Boolean).join(' ')
          || 'Unknown Owner'}
      </h3>

      {doc.restaurant?.name && (
        <p className="dl-card__sub">
          {doc.restaurant.name}{doc.restaurant.address?.city ? ` · ${doc.restaurant.address.city}` : ''}
        </p>
      )}

      <div className="dl-card__meta">
        <span>{fileCount} file{fileCount !== 1 ? 's' : ''}</span>
        <span className="dl-dot">·</span>
        <span>{doc.partner?.phone || doc.partner?.email || '—'}</span>
      </div>

      <div className="dl-card__foot">
        <span>Uploaded {timeAgo(latestUpload || doc.createdAt)}</span>
        <button className="dl-btn dl-btn--review" onClick={e => { e.stopPropagation(); onSelect(doc); }}>
          Review →
        </button>
      </div>
    </article>
  );
};

/* ════════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════ */
const DocumentsList = () => {
  const [tab,          setTab]          = useState('pending');
  const [category,     setCategory]     = useState('');
  const [searchInput,  setSearchInput]  = useState('');
  const [search,       setSearch]       = useState('');
  const [docs,         setDocs]         = useState([]);
  const [cursor,       setCursor]       = useState(null);
  const [hasMore,      setHasMore]      = useState(true);
  const [loading,      setLoading]      = useState(true);
  const [loadingMore,  setLoadingMore]  = useState(false);
  const [error,        setError]        = useState('');
  const [selectedDoc,  setSelectedDoc]  = useState(null);
  const [toast,        setToast]        = useState(null);
  const [stats,        setStats]        = useState({});

  const sentinelRef     = useRef(null);
  const searchDebounce   = useRef(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  /* ── Stats (tab badge counts) ────────────────────────── */
  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${host}/admin/documents/stats`, { credentials: 'include' });
      if (res.ok) setStats((await res.json()).stats);
    } catch { /* non-critical, ignore */ }
  }, []);

  /* ── Fetch a page (cursor-based) ─────────────────────── */
  const fetchPage = useCallback(async (cur, replace) => {
    replace ? setLoading(true) : setLoadingMore(true);
    setError('');
    try {
      const params = new URLSearchParams({ status: tab, limit: LIMIT });
      if (category) params.set('category', category);
      if (search)   params.set('search', search);
      if (cur)      params.set('cursor', cur);

      const res = await fetch(`${host}/admin/documents?${params}`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch documents');
      const data = await res.json();

      setDocs(prev => replace ? data.documents : [...prev, ...data.documents]);
      setHasMore(data.hasMore);
      setCursor(data.nextCursor);
    } catch (e) {
      setError(e.message || 'Error loading documents');
    } finally {
      replace ? setLoading(false) : setLoadingMore(false);
    }
  }, [tab, category, search]);

  /* ── Reset & reload on tab/category/search change ────── */
  useEffect(() => {
    setDocs([]);
    setCursor(null);
    setHasMore(true);
    fetchPage(null, true);
    fetchStats();
  }, [tab, category, search]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Debounce search input ───────────────────────────── */
  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(searchDebounce.current);
  }, [searchInput]);

  /* ── IntersectionObserver infinite scroll ────────────── */
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
          fetchPage(cursor, false);
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, cursor, fetchPage]);

  /* ── Callbacks from the review drawer ────────────────── */
  const handleReviewed = (updatedDoc) => {
    setDocs(prev => prev.map(d => d._id === updatedDoc._id ? { ...d, ...updatedDoc } : d));
    setSelectedDoc(updatedDoc);
    fetchStats();
  };

  const handleRemoveFromList = (docId) => {
    setDocs(prev => prev.filter(d => d._id !== docId));
    setSelectedDoc(null);
    fetchStats();
  };

  return (
    <div className="dl-root">
      {toast && (
        <div className={`dl-toast dl-toast--${toast.type}`}>
          {toast.type === 'success' ? '✓' : '✕'} {toast.msg}
        </div>
      )}

      {selectedDoc && (
        <AdminDocumentPreview
          docId={selectedDoc._id}
          currentTab={tab}
          onClose={() => setSelectedDoc(null)}
          onReviewed={handleReviewed}
          onToast={showToast}
          onStatusChangedAway={() => handleRemoveFromList(selectedDoc._id)}
        />
      )}

      <div className="dl-header">
        <div className="dl-tabs">
          {TABS.map(t => {
            const statKey = t.key === 'partial' ? 'partially_verified' : t.key;
            return (
              <button
                key={t.key}
                className={`dl-tab${tab === t.key ? ' dl-tab--active' : ''}`}
                onClick={() => setTab(t.key)}
              >
                <span className="dl-tab__icon">{t.icon}</span>
                {t.label}
                {t.key !== 'all' && stats[statKey] != null && (
                  <span className="dl-tab__count">{stats[statKey]}</span>
                )}
              </button>
            );
          })}
        </div>

        <div className="dl-filters">
          <div className="dl-search-wrap">
            <span className="dl-search__icon">🔍</span>
            <input
              className="dl-search"
              type="text"
              placeholder="Search by restaurant ID, owner ID, name, or phone..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <button className="dl-search__clear" onClick={() => setSearchInput('')}>✕</button>
            )}
          </div>

          <select className="dl-select" value={category} onChange={e => setCategory(e.target.value)}>
            {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
      </div>

      <div className="dl-body">
        {error && (
          <div className="dl-error">
            <span>⚠️</span> {error}
            <button className="dl-btn dl-btn--retry" onClick={() => fetchPage(cursor, false)}>Retry</button>
          </div>
        )}

        {loading ? (
          <div className="dl-grid">
            {[...Array(8)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : docs.length === 0 ? (
          <div className="dl-empty">
            <div className="dl-empty__icon">📭</div>
            <h3 className="dl-empty__title">No documents found</h3>
            <p className="dl-empty__sub">
              {search ? 'Try a different search term.' : 'Nothing in this queue right now.'}
            </p>
          </div>
        ) : (
          <>
            <div className="dl-grid">
              {docs.map(doc => (
                <DocCard key={doc._id} doc={doc} onSelect={setSelectedDoc} />
              ))}
            </div>

            {loadingMore && (
              <div className="dl-grid dl-grid--more">
                {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
              </div>
            )}

            <div ref={sentinelRef} className="dl-sentinel" aria-hidden="true" />

            {!hasMore && !loadingMore && (
              <p className="dl-end-msg">You've reached the end · {docs.length} loaded</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DocumentsList;